import React, { useState, useContext, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { AuthContext } from '../../providers/AuthProvider';
import './CompanyRegistrationForm.css';
import CompanyRegistrationSuccess from './CompanyRegistrationSuccess';
import logo from '../../assets/ecomlovers-logo-transparente.svg';
import CompanyFormFields from '../../components/CompanyFormFields/CompanyFormFields';

const CompanyRegistrationForm = () => {
  const { signUpCompany } = useContext(AuthContext);
  const registrationCompletedRef = useRef(false);

  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    role: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    const registrationComplete = sessionStorage.getItem('companyRegistrationComplete');
    if (registrationComplete === 'true') {
      setShowSuccess(true);
      setIsRegistered(true);
      registrationCompletedRef.current = true;
    }
  }, []);

  const formatPhoneNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 11);

    if (digits.length === 0) {
      return "";
    }

    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 7);
    const part3 = digits.slice(7, 11);

    let formatted = "";
    if (part1) formatted += `(${part1}`;
    if (part2) formatted += `) ${part2}`;
    if (part3) formatted += `-${part3}`;
    return formatted;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const currentDigits = value.replace(/\D/g, '');
      const previousDigits = formData.phone.replace(/\D/g, '');

      if (currentDigits.length < previousDigits.length) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      } else {
        const masked = formatPhoneNumber(value);
        setFormData(prev => ({
          ...prev,
          [name]: masked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      setMessage('Nome da empresa é obrigatório');
      setMessageType('error');
      return false;
    }

    if (!formData.name.trim()) {
      setMessage('Nome completo é obrigatório');
      setMessageType('error');
      return false;
    }

    if (!formData.email.trim()) {
      setMessage('E-mail é obrigatório');
      setMessageType('error');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('E-mail inválido');
      setMessageType('error');
      return false;
    }

    if (!formData.phone.trim()) {
      setMessage('Telefone é obrigatório');
      setMessageType('error');
      return false;
    }

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 11) {
      setMessage('Telefone deve ter pelo menos 11 dígitos');
      setMessageType('error');
      return false;
    }

    if (!formData.role) {
      setMessage('Departamento é obrigatório');
      setMessageType('error');
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (isRegistered && !showSuccess) {
      setShowSuccess(true);
    }
  }, [isRegistered, showSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const formattedPhone = `+55 (${phoneDigits.slice(0, 2)}) ${phoneDigits.slice(2, 3)} ${phoneDigits.slice(3, 7)}-${phoneDigits.slice(7, 11)}`;

      const { data } = await api.post('/companies/companies', {
        data: {
          type: 'companies',
          attributes: {
            name: formData.name.trim(),
            phone: formattedPhone,
            email: formData.email.trim(),
            companyName: formData.companyName.trim(),
            companyPhone: formattedPhone,
            companyEmail: formData.email.trim(),
            role: formData.role.trim()
          }
        }
      });

      if (data?.data) {
        const response = await signUpCompany(data);

        if (response === 'success') {
          sessionStorage.setItem('companyRegistrationComplete', 'true');
          registrationCompletedRef.current = true;

          setLoading(false);
          setIsRegistered(true);
          setShowSuccess(true);
        } else {
          setLoading(false);
          setMessage('Erro ao cadastrar empresa. Tente novamente.');
          setMessageType('error');
        }
      }

    } catch (error) {
      setLoading(false);
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        let errorMessages = [];

        errors.forEach(err => {
          if (err.id === 'email' && err.title === 'Email já está em uso') {
            errorMessages.push('Este e-mail já está cadastrado em nossa plataforma.');
          } else if (err.id === 'phone' && err.title === 'Phone já está em uso') {
            errorMessages.push('Este telefone já está cadastrado em nossa plataforma.');
          } else {
            errorMessages.push(err.title || err.detail || 'Erro de validação');
          }
        });

        setMessage(errorMessages.join('\n'));
        setMessageType('error');
      } else if (error.response && error.response.data && error.response.data.error) {
        setMessage(error.response.data.error);
        setMessageType('error');
      } else {
        setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
        setMessageType('error');
      }
    }
  };

  const shouldShowSuccess = showSuccess || isRegistered || registrationCompletedRef.current;
  if (shouldShowSuccess) {
    return <CompanyRegistrationSuccess />;
  }

  return (
    <div className="company-registration-container">
      <img src={logo} alt="eComLovers Logo" className="company-logo" />
      <div className="company-registration-form">

      <div className="form-header-section-container">
        <h2>Acesse já!</h2>
        <p className="form-header-submessage">Preencha os campos abaixo para cadastrar sua empresa na eComLovers.</p>
      </div>

      <form onSubmit={handleSubmit} className="company-form" noValidate>
        <div className="form-section">
          <CompanyFormFields
            formData={formData}
            handleInputChange={handleInputChange}
            message={message}
            messageType={messageType}
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Cadastrando...' : 'Cadastrar Empresa'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default CompanyRegistrationForm;

